/**
 * Opt-in usage statistics (decision 0017, services/api/src/stats.ts). Off by default; on from
 * Settings. Counts the features used — menu commands, by their stable id — per day, and sends
 * the counts (no account, no project data) every ten minutes and when the app is hidden.
 */
import type { Platform } from '@elec/platform';

export const USAGE_PREF = 'usageStats';
const EVENT = /^[a-z0-9][a-z0-9:._-]{0,59}$/;

let on = false;
let counts = new Map<string, number>();
const day = () => new Date().toISOString().slice(0, 10);
let countsDay = day();
let sender: (() => void) | null = null;

export const usageEnabled = (platform: Platform) => platform.getPref(USAGE_PREF) === '1';

export function setUsage(platform: Platform, enabled: boolean): void {
  platform.setPref(USAGE_PREF, enabled ? '1' : '0');
  on = enabled;
  if (!enabled) counts = new Map();
}

/** Count a feature used ("menu:export-pdf"). Ignored unless the user opted in. */
export function track(event: string): void {
  if (!on || !EVENT.test(event)) return;
  if (countsDay !== day()) sender?.();
  counts.set(event, (counts.get(event) ?? 0) + 1);
}

/** The counts not sent yet (tests). */
export const pendingUsage = () => Object.fromEntries(counts);

/** Start sending (once). `baseUrl` undefined: nothing is sent (no server). */
export function startUsage(opts: {
  platform: Platform;
  baseUrl: string | undefined;
  version: string;
  locale: () => string;
  fetch?: typeof fetch;
}): () => void {
  on = usageEnabled(opts.platform);
  const f = opts.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const send = () => {
    if (!on || !counts.size || !opts.baseUrl) return;
    const events = Object.fromEntries(counts);
    const body = {
      day: countsDay,
      app: opts.platform.kind,
      version: opts.version.replace(/[^\w.+-]/g, '').slice(0, 20) || '0',
      locale: ['fr', 'en', 'nl', 'de'].includes(opts.locale()) ? opts.locale() : 'fr',
      events,
    };
    counts = new Map();
    countsDay = day();
    void f(`${opts.baseUrl}/api/stats`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {
      // Offline: put the counts back for the next try.
      for (const [k, n] of Object.entries(events)) counts.set(k, (counts.get(k) ?? 0) + n);
    });
  };
  sender = send;
  const timer = setInterval(send, 10 * 60 * 1000);
  const hidden = () => document.visibilityState === 'hidden' && send();
  document.addEventListener('visibilitychange', hidden);
  return () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', hidden);
    sender = null;
  };
}
